import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { detailFromAxiosError, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, FileDown, Upload, ClipboardPaste, CreditCard } from "lucide-react";
import { daysLeftUntilValidTo, LICENSE_RENEWAL_REMINDER_DAYS } from "@/lib/licenseRenewal";
import { CONFIGURACION_DESPACHO_PATH } from "@/modules/config/configuracionRoutes";
import LicenseRequestPanel from "@/components/LicenseRequestPanel";
import {
  canShowCommercialLicenseRequestUi,
  hasActiveCommercialLicense,
} from "@/lib/commercialLicenseRequests";
import { useAuth } from "@/lib/auth";
import { useWorkspaceMode } from "@/lib/workspaceMode";

/**
 * Activación dual: solicitud en línea + Stripe (placeholder) + flujo manual PDF/clave.
 */
export default function LicenseActivationPanel({
  systemInfo,
  onLicenseInstalled,
  showAlerts = true,
  profileDefaults = null,
  showOnlineRequest = true,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mode, canSwitch } = useWorkspaceMode();
  const activacionPdfInputRef = useRef(null);
  const [busySolicitudPdf, setBusySolicitudPdf] = useState(false);
  const [busyActivacionPdf, setBusyActivacionPdf] = useState(false);

  const sub = systemInfo?.subscription || {};
  const trialDaysConfigured = Number(systemInfo?.trial_days) > 0 ? Number(systemInfo.trial_days) : 3;
  const validToEarly = systemInfo?.license?.payload?.valid_to;
  const daysLeftLicense = daysLeftUntilValidTo(validToEarly);
  const inLicenseRenewalWindow =
    typeof daysLeftLicense === "number" &&
    daysLeftLicense <= LICENSE_RENEWAL_REMINDER_DAYS &&
    daysLeftLicense >= 0;
  const supportEmail = (systemInfo?.billing?.support_email || "").trim();

  const showOnlineChannel =
    showOnlineRequest
    && canShowCommercialLicenseRequestUi(user, { workspaceMode: mode, canSwitch });
  const hideManualActivationChannel =
    showOnlineChannel && hasActiveCommercialLicense(systemInfo);

  const downloadSolicitudLicenciaPdf = async () => {
    setBusySolicitudPdf(true);
    try {
      const { data } = await api.get("/system/license/solicitud-pdf", { responseType: "blob" });
      const safeId = String(systemInfo?.installation_id || "visor").replace(/[^a-zA-Z0-9_-]/g, "-");
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `solicitud-licencia-${safeId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF de solicitud descargado");
    } catch (err) {
      toast.error(
        (await detailFromAxiosError(err)) ||
          formatApiError(err.response?.data?.detail) ||
          "Error al generar el PDF de solicitud",
      );
    } finally {
      setBusySolicitudPdf(false);
    }
  };

  const onImportActivacionLicenciaPdf = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Selecciona un archivo PDF");
      return;
    }
    setBusyActivacionPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/system/license/import-activacion-pdf", fd);
      toast.success("Licencia activada desde el PDF");
      const { data: infoData } = await api.get("/system/info");
      onLicenseInstalled?.({ info: infoData, postInstallNotice: Boolean(data?.post_install_data_notice) });
    } catch (err) {
      toast.error(
        (await detailFromAxiosError(err)) ||
          formatApiError(err.response?.data?.detail) ||
          "No se pudo leer o validar el PDF de activación",
      );
    } finally {
      setBusyActivacionPdf(false);
    }
  };

  const onStripePlaceholder = () => {
    toast.info(
      "Pago en línea disponible próximamente. Use el PDF de solicitud o contacte a soporte mientras tanto.",
    );
  };

  return (
    <div className="space-y-3" data-testid="license-activation-panel">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        Activar / renovar servicio
      </div>

      {showAlerts && sub.mode === "trial" && sub.ok ? (
        <div className="rounded-sm border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-950 leading-snug">
          <span className="font-semibold">Período de prueba ({trialDaysConfigured} días)</span>
          {" — "}Suscríbete en línea (próximamente) o solicite licencia al administrador.
        </div>
      ) : null}
      {showAlerts && sub.mode === "trial" && !sub.ok && sub.reason === "trial_expired" ? (
        <div className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-950 leading-snug">
          <span className="font-semibold">Prueba vencida.</span> El periodo de prueba de esta instalación ya
          venció. Solicita activación de licencia con el PDF del distribuidor o en Configuración.
        </div>
      ) : null}
      {showAlerts && inLicenseRenewalWindow && typeof daysLeftLicense === "number" ? (
        <div className="rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-950 leading-snug">
          <span className="font-semibold">Renovación</span>
          {" — "}Su licencia vence en <strong>{daysLeftLicense}</strong>{" "}
          {daysLeftLicense === 1 ? "día" : "días"} (hasta {String(validToEarly).slice(0, 10)}).
        </div>
      ) : null}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 w-full sm:w-auto text-xs font-semibold border-indigo-300 text-indigo-900 bg-indigo-50/50 opacity-75 cursor-not-allowed"
        disabled
        onClick={onStripePlaceholder}
        title="Próximamente"
      >
        <CreditCard className="w-3.5 h-3.5 mr-1.5" />
        Pagar con Stripe (En construcción)
      </Button>
      <p className="text-[10px] text-slate-500 leading-snug">
        {hideManualActivationChannel
          ? "Tu licencia web ya está activa. Para renovación o módulos adicionales usa la solicitud en línea."
          : "Próximamente podrás suscribirte en línea. Mientras tanto, use el flujo manual abajo."}
      </p>

      {showOnlineChannel ? (
        <div className="rounded-sm border border-[#0F2D5C]/20 bg-slate-50/80 px-3 py-3 space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#0F2D5C] m-0">
            Canal en línea
          </p>
          <p className="text-[10px] text-slate-600 m-0 leading-snug">
            Envía una solicitud formal a BNT desde el sistema.
          </p>
          <LicenseRequestPanel
            systemInfo={systemInfo}
            profileDefaults={profileDefaults}
            showSectionHeader={false}
          />
        </div>
      ) : null}

      {!hideManualActivationChannel ? (
        <>
          <div className="flex items-center gap-2 text-[10px] text-slate-400 uppercase tracking-wider">
            <span className="flex-1 border-t border-slate-200" />
            <span>o</span>
            <span className="flex-1 border-t border-slate-200" />
          </div>

          <div className="space-y-2" data-testid="license-activation-traditional-channel">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 m-0">
              Canal tradicional
            </p>
            <p className="text-[10px] text-slate-500 m-0 leading-snug">
              Descarga la solicitud en PDF para enviarla por correo o WhatsApp.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 text-xs font-semibold border-[#0F2D5C] text-[#0F2D5C] hover:bg-[#0F2D5C]/5"
                disabled={busySolicitudPdf}
                onClick={() => downloadSolicitudLicenciaPdf()}
                data-testid="license-activation-pdf-solicitud"
              >
                {busySolicitudPdf ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <FileDown className="w-3.5 h-3.5 mr-1.5" />
                )}
                Solicitar licencia por PDF
              </Button>
              <input
                ref={activacionPdfInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={onImportActivacionLicenciaPdf}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 text-xs font-semibold border-emerald-700 text-emerald-800 hover:bg-emerald-50"
                disabled={busyActivacionPdf}
                onClick={() => activacionPdfInputRef.current?.click()}
              >
                {busyActivacionPdf ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                )}
                Activar licencia por PDF
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 text-xs font-semibold border-slate-400 text-slate-800 hover:bg-slate-50"
                onClick={() => navigate(`${CONFIGURACION_DESPACHO_PATH}?activar_licencia=1`)}
              >
                <ClipboardPaste className="w-3.5 h-3.5 mr-1.5" />
                Activar manualmente
              </Button>
            </div>
          </div>
        </>
      ) : null}

      {supportEmail ? (
        <p className="text-[10px] text-slate-500">
          Contacto:{" "}
          <a href={`mailto:${supportEmail}`} className="text-[#0F2D5C] underline font-medium">
            {supportEmail}
          </a>
        </p>
      ) : null}
    </div>
  );
}
